import { useMemo } from "react";
import { useVersionModulesList } from "../../../../../query/hooks/QueryHooksLinkedProgrammes";
import { useAllModulesNameById, useNetsuiteItems } from "../../../../../query/hooks/QueryHooksVersionDetails";
import { invalidateSummaryQueries } from "../../../../../query/hooks/QueryHooksProgramDetail";
import useVersionModuleBulkActions from "./useVersionModuleBulkActions";
import useVersionModuleRows from "./useVersionModuleRows";

/**
 * Drives the Version Modules table (SPEC3): client-side search/status/term/year
 * filtering, sorting and pagination (`useVersionModuleRows`) over the unpaginated module
 * list. Row selection and the bulk "Unlink Modules" flow live in `useVersionModuleBulkActions`.
 * @param {string | number} programmeId PMC programme id.
 * @param {string | number} versionId Version id to scope modules to.
 * @param {boolean} [isLocked] Whether the version's settings are locked - blocks unlinking.
 */
function useVersionModules({ programmeId, versionId, isLocked = false }) {
    const {
        data: moduleRows,
        isLoading: loading,
        isFetching,
        isError,
        refetch,
    } = useVersionModulesList(programmeId, versionId);

    const refresh = async () => {
        await Promise.all([refetch(), invalidateSummaryQueries()]);
    };

    const { items: netsuiteList, isLoading: isNetsuiteLoading } = useNetsuiteItems();

    /*
     * Prerequisite ids resolve to names against every module in the system, since a
     * saved prerequisite can belong to a different programme/version than the one
     * currently being viewed - scoping this to the version's rows hid any such
     * cross-version prerequisite behind a "-".
     */
    const { modules: allModules } = useAllModulesNameById();
    const moduleNameById = useMemo(() => {
        const map = new Map();
        allModules.forEach((module) => map.set(module.id, module.name));
        return map;
    }, [allModules]);

    const tableState = useVersionModuleRows(moduleRows);

    const allMatchingIds = useMemo(() => tableState.allFilteredRows.map((row) => row.Id), [tableState.allFilteredRows]);

    const bulkActions = useVersionModuleBulkActions({
        programmeId,
        versionId,
        isLocked,
        allMatchingIds,
        filterSignature: tableState.filterSignature,
        isRefreshing: isFetching,
        onUnlinked: refresh,
    });

    return {
        ...tableState,
        loading,
        isError,
        netsuiteList,
        isNetsuiteLoading,
        moduleNameById,
        isLocked,
        ...bulkActions,
        refresh,
    };
}

export default useVersionModules;
